/**
 * PHARMAKON — Le TOAST PERSISTANT du palier « important » (WARNING).
 *
 * PLAN_mobile_pharma_owner_manager §3.5, ligne « Important ».
 *
 * CONTRAT : persistant — il ne s'efface JAMAIS tout seul ; fermeture explicite
 * seulement ; le geste de fermeture écrit `seen_at` (`POST /:id/vu`). Il ne
 * bloque ni le focus ni la navigation.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * POURQUOI CE N'EST PAS UN TOAST DU `ToastHost` PARTAGÉ — écart assumé.
 *
 * Le plan proposait d'ajouter une variante « sticky » (`duration: null`) au
 * `ToastProvider` existant. Deux raisons de ne pas le faire :
 *
 *  1. LE TOAST PARTAGÉ NE PORTE QU'UNE CHAÎNE. `addToast(message, type,
 *     duration)` et le rendu `<span>{t.message}</span>` : il n'y a pas de place
 *     pour un titre, un auteur, un horodatage NI pour un bouton de fermeture
 *     qui doit appeler l'API. Les faire entrer aurait voulu dire réécrire le
 *     contrat de la file de toasts — le contraire d'« additif ».
 *  2. LE CYCLE DE VIE EST À L'ENVERS. Un toast partagé est POUSSÉ une fois puis
 *     oublié ; celui-ci est DÉRIVÉ d'un état serveur re-sondé toutes les 15 s.
 *     Le pousser à chaque tour de sondage produirait des doublons ; le pousser
 *     une seule fois voudrait dire qu'une annulation côté titulaire (§3.3, le
 *     coupe-circuit) ne pourrait plus le retirer de l'écran.
 *
 * On rend donc les alertes dans LEUR PROPRE couche, à la même ancre visuelle
 * que `ToastHost` (haut-droite) et sous lui en profondeur, pour que les deux
 * s'empilent proprement. RIEN n'est changé au toast partagé — aucun autre écran
 * ne peut régresser.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import * as React from "react";
import { AlertCircle, X } from "lucide-react";
import { LIBELLE_PALIER } from "./alerte-equipe.config.js";

/**
 * @param {object} p
 * @param {object[]} p.alertes
 * @param {(id: string) => void} p.onFermer écrit `seen_at`
 */
export default function AlerteToastPersistant({ alertes = [], onFermer }) {
  if (alertes.length === 0) return null;

  return (
    <div
      className="pk-alerte-toasts"
      data-testid="alerte-toasts"
      // `polite` et pas `assertive` : ce palier informe, il n'interrompt pas.
      aria-live="polite"
      aria-label="Alertes équipe importantes"
    >
      {alertes.map((a) => (
        <div
          key={a.id}
          role="status"
          className="pk-alerte-toast"
          data-testid="alerte-toast"
          data-alerte-id={a.id}
          // Marqueur lu par le test E2E : ce toast n'a AUCUNE minuterie.
          data-persistant="true"
        >
          <AlertCircle size={16} aria-hidden="true" className="pk-alerte-toast-icone" />
          <div className="pk-alerte-toast-corps">
            <div className="pk-alerte-toast-entete">
              <span className="pk-alerte-toast-palier">{LIBELLE_PALIER.WARNING}</span>
              <span className="pk-alerte-toast-titre">{a.titre}</span>
            </div>
            <p className="pk-alerte-toast-message">{a.message}</p>
          </div>
          <button
            type="button"
            className="pk-alerte-toast-fermer"
            data-testid="alerte-toast-fermer"
            aria-label={`Fermer l'alerte « ${a.titre} »`}
            onClick={() => onFermer?.(a.id)}
          >
            <X size={14} aria-hidden="true" />
          </button>
        </div>
      ))}
    </div>
  );
}
